import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Chip,
  makeStyles
} from "@material-ui/core";
import {
  GroupAdd as GroupAddIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from "@material-ui/icons";
import useSound from "use-sound";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    padding: theme.spacing(1),
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    border: `2px solid ${theme.palette.primary.main}`,
  },
  dialogTitle: {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.12)" : "rgba(25, 118, 210, 0.08)",
    borderRadius: 12,
    margin: theme.spacing(1),
  },
  contentBox: {
    padding: theme.spacing(2),
  },
  invitationCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.action.hover,
    marginTop: theme.spacing(1.5),
  },
}));

const TicketInvitationModal = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [playAlert] = useSound(alertSound, {
    volume: 0.5,
  });
  const [currentInvite, setCurrentInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!socket || !user?.id || !user?.companyId) return;

    const eventName = `company-${user.companyId}-ticketInvitation`;

    const handleInvitation = (data) => {
      // Se for um convite criado para o usuário atual
      if (data.action === "create" && data.targetUserId === user.id) {
        try {
          playAlert();
        } catch (e) {
          // ignore sound error
        }

        if (isMounted.current) {
          setCurrentInvite(data);
        }
      }
    };

    socket.on(eventName, handleInvitation);

    return () => {
      socket.off(eventName, handleInvitation);
    };
  }, [socket, user, playAlert]);

  const handleAccept = async () => {
    if (!currentInvite) return;
    setLoading(true);
    try {
      const ticketId = currentInvite.ticketId;
      const { data } = await api.post(`/tickets/${ticketId}/participants/accept`);
      toastUtils.success("Você entrou no atendimento com sucesso!");
      setCurrentInvite(null);

      // Redirecionar para o ticket se tiver uuid ou id
      const targetUuid = data?.ticket?.uuid || currentInvite?.invitation?.ticket?.uuid;
      if (targetUuid) {
        history.push(`/tickets/${targetUuid}`);
      } else {
        history.push(`/tickets/${ticketId}`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!currentInvite) return;
    setLoading(true);
    try {
      const ticketId = currentInvite.ticketId;
      await api.post(`/tickets/${ticketId}/participants/reject`);
      toastUtils.info("Convite recusado.");
      setCurrentInvite(null);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  if (!currentInvite) return null;

  const inviterName = currentInvite.inviter?.name || "Um atendente";
  const contactName = currentInvite.invitation?.ticket?.contact?.name || "Cliente";
  const queueName = currentInvite.invitation?.ticket?.queue?.name;
  const queueColor = currentInvite.invitation?.ticket?.queue?.color || "#1976d2";
  const ticketId = currentInvite.ticketId;

  return (
    <Dialog
      open={Boolean(currentInvite)}
      onClose={() => setCurrentInvite(null)}
      maxWidth="xs"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupAddIcon color="primary" />
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Convite para Co-atendimento
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.contentBox}>
        <Typography variant="body2" color="textPrimary">
          <strong>{inviterName}</strong> convidou você para participar do atendimento:
        </Typography>

        <Box className={classes.invitationCard}>
          <Avatar style={{ backgroundColor: "#1976d2", color: "#fff" }}>
            {contactName.charAt(0)}
          </Avatar>
          <Box flex={1}>
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {contactName}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Typography variant="caption" color="textSecondary">
                Ticket #{ticketId}
              </Typography>
              {queueName && (
                <Chip
                  label={queueName}
                  size="small"
                  style={{
                    backgroundColor: queueColor,
                    color: "#fff",
                    height: 18,
                    fontSize: "0.65rem",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: "12px 16px", justifyContent: "space-between" }}>
        <Button
          onClick={handleReject}
          color="secondary"
          variant="outlined"
          disabled={loading}
          startIcon={<CloseIcon />}
        >
          Recusar
        </Button>

        <ButtonWithSpinner
          onClick={handleAccept}
          color="primary"
          variant="contained"
          loading={loading}
          startIcon={<CheckIcon />}
        >
          Aceitar e Entrar
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TicketInvitationModal;
